<template>
  <!--
    PoolaBalancePill — a compact "what you can spend right now" strip for the
    ACTIVE run: available cash + available «بولشير». A CONTENT FRAGMENT.

    Seeded from useWallets() in the run, then re-fed from a claim's
    `meta.balances` (the run swaps in the authoritative post-claim figures, no
    extra wallet read). A null figure renders an em dash rather than a misleading
    "0.00" while the first wallet read is still in flight.
  -->
  <div class="poola-balance-pill" role="status">
    <div class="poola-balance-pill__item">
      <span class="poola-balance-pill__label">{{ t('poola.buy.card.cashLabel') }}</span>
      <span class="poola-balance-pill__value">{{ cashDisplay }}</span>
    </div>
    <span class="poola-balance-pill__divider" aria-hidden="true">·</span>
    <div class="poola-balance-pill__item poola-balance-pill__item--poolshare">
      <span class="poola-balance-pill__label">{{ t('poola.buy.card.poolshareLabel') }}</span>
      <span class="poola-balance-pill__value">{{ poolshareDisplay }}</span>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCurrency } from '@/composables/useCurrency.js';

const props = defineProps({
  // Available spendable balances in integer cents, or null while unknown.
  cashCents: {
    type: Number,
    default: null,
  },
  poolshareCents: {
    type: Number,
    default: null,
  },
});

const { t } = useI18n({ useScope: 'global' });
const { formatCurrency } = useCurrency();

const cashDisplay = computed(() =>
  props.cashCents == null ? '—' : formatCurrency(props.cashCents),
);
const poolshareDisplay = computed(() =>
  props.poolshareCents == null ? '—' : formatCurrency(props.poolshareCents),
);
</script>

<style scoped>
.poola-balance-pill {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 0 16px 12px;
  padding: 8px 14px;
  background: var(--poolit-bg-primary);
  border: 1px solid var(--poolit-border-color);
  border-radius: var(--poolit-radius-full);
  box-shadow: var(--poolit-shadow-soft);
}

.poola-balance-pill__item {
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
}

.poola-balance-pill__label {
  font-size: var(--poolit-font-size-small);
  color: var(--poolit-text-secondary);
  font-weight: var(--poolit-font-weight-medium);
}

.poola-balance-pill__value {
  font-size: var(--poolit-font-size-body);
  font-weight: var(--poolit-font-weight-bold);
  color: var(--poolit-text-primary);
  font-variant-numeric: tabular-nums;
}

.poola-balance-pill__item--poolshare .poola-balance-pill__value {
  color: var(--poolit-primary-700);
}

.poola-balance-pill__divider {
  color: var(--poolit-text-muted);
}
</style>
